// ============================================================================
// D4 resolution dialog (Option D PR-4a). When a new-account card's start date
// passes without a confirmed start, it becomes a RED "⚠ Behind" item. Opening
// it shows this blocking [error → fix needed] dialog that forces the data back
// into truth — exactly one of three paths:
//
//   1. It started        → confirm_account_started with an EDITABLE actual
//                           start date (default = planned). This date drives
//                           billing, the annual kicker, and proration.
//   2. Start date moved  → push planned_start_date to a new future date
//                           (re-arms the countdown, clears the RED flag).
//   3. Cancelled         → cancel the card (off the board).
//
// Reused on the New Accounts board, the dashboard widget, and ACCOUNTS (PR-5).
// ============================================================================

import { useState } from 'react';
import { AlertTriangle, CalendarClock, CheckCircle2, XCircle } from 'lucide-react';
import { Button, useToast } from '../../shared/components';
import { confirmAccountStarted } from './hooks/use-sop-data';
import { StartingServicesFields, useStartingServices } from './starting-services';
import { StartDateMoveFields, useStartDateMove } from './start-date-move-fields';
import { errorMessage } from '../../shared/errors';

export interface ResolutionTarget {
  sopId: string;
  facilityId: string | null;
  displayName: string;
  plannedStartDate: string | null;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export function ResolutionDialog({
  target,
  onClose,
  onResolved,
  onCancelCard,
}: {
  target: ResolutionTarget;
  onClose: () => void;
  /** Called after a successful resolution so the parent can refetch. */
  onResolved: () => void;
  /** Cancel the card (parent owns the patch: status/stage = Cancelled + reason). */
  onCancelCard: (sopId: string, reason: string) => Promise<void>;
}) {
  const toast = useToast();
  const [mode, setMode] = useState<'choose' | 'started' | 'moved' | 'cancelled'>('choose');
  const [actualDate, setActualDate] = useState<string>(target.plannedStartDate ?? todayISO());
  /** Cancelling the card has its own reason, written to the CARD. The moved
   *  path's reason is the BUILDING's and lives in the shared fields. */
  const [cancelReason, setCancelReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const noFacility = !target.facilityId;
  // Which services start with it (Ben, 2026-10-23) — the same piece the Account start card uses.
  const starting = useStartingServices(target.facilityId);

  // ⛔ ONE COMPONENT OWNS THIS WRITE. This path worked; it works the same way
  // now, through the piece the Account start card also uses, so there is no
  // second hand-written copy of the reason field to drift. The date box seeds
  // TODAY here, not the planned date — this dialog only opens on a building
  // whose planned date has already passed.
  const move = useStartDateMove({
    facilityId: target.facilityId,
    initialDate: todayISO(),
    onSaved: () => {
      onResolved();
      onClose();
    },
  });

  async function saveMove() {
    setError(null);
    const { ok, error: err } = await move.submit();
    // ⛔ The dialog STAYS OPEN with the typed values in it and the sentence
    // renders inside it, never in page-level state (TCSD-0044).
    if (!ok) setError(err);
  }

  async function run(fn: () => Promise<void>, successMsg: string) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      toast.success(successMsg);
      onResolved();
      onClose();
    } catch (e) {
      // Supabase RPC errors are PostgrestError objects, not Error instances.
      // errorMessage() is the shared unwrapper (AFB-0054) — it also translates
      // constraint violations, which the hand-rolled version here did not.
      setError(errorMessage(e, 'Something went wrong.', 'sop-resolution'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-tcs-scrim/60" />
      <div className="relative z-10 w-full max-w-md rounded-t-2xl bg-tcs-bg p-5 shadow-2xl sm:rounded-2xl">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-rag-red/10 text-rag-red">
            <AlertTriangle className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-wide text-rag-red">Needs attention</div>
            <h2 className="text-base font-semibold text-tcs-text">{target.displayName}</h2>
            <p className="mt-1 text-sm text-tcs-text-secondary">
              This account's start date was{' '}
              <strong className="text-tcs-text">
                {target.plannedStartDate ?? 'not set'}
              </strong>{' '}
              and it was never confirmed as started. Did it actually start, or did the start date move?
            </p>
          </div>
        </div>

        {error && (
          <div className="mt-3 rounded-md border border-st-danger-border bg-st-danger-bg px-3 py-2 text-sm text-rag-red">
            {error}
          </div>
        )}

        {mode === 'choose' && (
          <div className="mt-4 space-y-2">
            <button
              type="button"
              disabled={noFacility}
              onClick={() => setMode('started')}
              className="flex w-full items-center gap-3 rounded-lg border border-tcs-border p-3 text-left transition-colors hover:bg-tcs-bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            >
              <CheckCircle2 className="h-5 w-5 shrink-0 text-rag-green" />
              <span>
                <span className="block text-sm font-medium text-tcs-text">It started</span>
                <span className="block text-xs text-tcs-text-secondary">
                  {noFacility ? 'No linked facility — cannot confirm here.' : 'Set the real start date and mark it live.'}
                </span>
              </span>
            </button>
            <button
              type="button"
              disabled={noFacility}
              onClick={() => setMode('moved')}
              className="flex w-full items-center gap-3 rounded-lg border border-tcs-border p-3 text-left transition-colors hover:bg-tcs-bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            >
              <CalendarClock className="h-5 w-5 shrink-0 text-tcs-accent-ink" />
              <span>
                <span className="block text-sm font-medium text-tcs-text">Start date moved</span>
                <span className="block text-xs text-tcs-text-secondary">Pick a new planned start date (re-arms the countdown).</span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => setMode('cancelled')}
              className="flex w-full items-center gap-3 rounded-lg border border-tcs-border p-3 text-left transition-colors hover:bg-tcs-bg-muted"
            >
              <XCircle className="h-5 w-5 shrink-0 text-tcs-text-muted" />
              <span>
                <span className="block text-sm font-medium text-tcs-text">Cancelled</span>
                <span className="block text-xs text-tcs-text-secondary">This account isn't happening — remove it from the board.</span>
              </span>
            </button>
            <button type="button" onClick={onClose} className="w-full pt-1 text-center text-xs text-tcs-text-muted hover:text-tcs-text-secondary">
              I'll handle this later
            </button>
          </div>
        )}

        {mode === 'started' && (
          <div className="mt-4 space-y-3">
            <label className="block">
              <span className="text-xs font-medium text-tcs-text-secondary">Actual start date (drives billing, kicker, proration)</span>
              <input
                type="date"
                value={actualDate}
                onChange={(e) => setActualDate(e.target.value)}
                className="mt-1 w-full rounded-md border border-tcs-border bg-tcs-bg px-3 py-2 text-sm text-tcs-text focus:outline-none focus:ring-2 focus:ring-tcs-accent"
              />
            </label>
            <StartingServicesFields state={starting} />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => setMode('choose')} disabled={busy}>Back</Button>
              <Button
                size="sm"
                disabled={busy || !actualDate || !target.facilityId || starting.loading || !!starting.error || !starting.ready}
                onClick={() => run(() => confirmAccountStarted(target.facilityId as string, actualDate, starting.items), 'Account marked started')}
              >
                Confirm started
              </Button>
            </div>
          </div>
        )}

        {mode === 'moved' && (
          <div className="mt-4 space-y-3">
            <StartDateMoveFields move={move} />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => setMode('choose')} disabled={move.busy}>Back</Button>
              <Button size="sm" disabled={move.busy || !move.ready} onClick={saveMove}>
                {move.draft.dateUnknown ? 'Record it' : 'Update start date'}
              </Button>
            </div>
          </div>
        )}

        {mode === 'cancelled' && (
          <div className="mt-4 space-y-3">
            <label className="block">
              <span className="text-xs font-medium text-tcs-text-secondary">Reason</span>
              <input
                type="text"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Why is this account cancelled?"
                className="mt-1 w-full rounded-md border border-tcs-border bg-tcs-bg px-3 py-2 text-sm text-tcs-text focus:outline-none focus:ring-2 focus:ring-tcs-accent"
              />
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => setMode('choose')} disabled={busy}>Back</Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={busy || cancelReason.trim().length === 0}
                onClick={() => run(() => onCancelCard(target.sopId, cancelReason.trim()), 'Card cancelled')}
              >
                Cancel account
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
